import BaseLayout from '@/layout/BaseLayout';
import { Badge } from '@shelflix/ui-web/components/ui/badge';
import type { MaterialTypeDto } from '@shelflix/trpc/routes/materialType/list/useCase';
import { ConfirmationModal } from '@shelflix/ui-web/components/ConfirmationModal';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@shelflix/ui-web/components/Icon';
import { GenericTable } from '@shelflix/ui-web/components/GenericTable';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { trpc } from '@/trpcClient';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialTypeFilter } from './MaterialTypeFilter';
import type { ListMaterialTypeFilterTypeWithValues } from '@shelflix/schemas/materialType/list';
import { useHasScopes } from '@/hooks/useHasScopes';

export function MaterialTypeList() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [filters, setFilters] = useState<ListMaterialTypeFilterTypeWithValues>({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const canDeleteMaterialType = useHasScopes(['admin:material_type:manage']);
  const deleteMaterialTypeMutation = useMutation(trpc.materialType.delete.mutationOptions());

  const { data, isLoading, isError, refetch } = useQuery(
    trpc.materialType.list.queryOptions({
      page,
      recordsPerPage: pageSize,
      ...(filters.search?.value && { search: { ...filters.search } }),
      ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
      ...(filters.sort && { sort: filters.sort }),
    })
  );

  const materialTypes = data?.items ?? [];
  const totalItems = data?.total ?? 0;

  const columns: Array<{
    header: string;
    key: keyof MaterialTypeDto;
    render?: (value: MaterialTypeDto[keyof MaterialTypeDto], row: MaterialTypeDto) => React.ReactNode;
  }> = [
    {
      header: 'Nome do material',
      key: 'name',
      render: (value, row) => (
        <div className="flex min-w-[200px] items-center gap-4">
          {/* TODO deixar imagem padrão de fallback */}
          <img
            className="contain h-10 w-10"
            src={row.photoUrl}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src =
                'https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png'; //TODO colocar um fallback padrão
            }}
          />
          <span
            className="[font-family:'Figtree',Helvetica] text-base font-medium text-[var(--gray-primary)]"
            style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
          >
            {value.toString()}
          </span>
        </div>
      ),
    },
    {
      header: 'Marca',
      key: 'brand',
      render: (value, row) => (
        <div className="flex flex-col">
          <span className="[font-family:'Figtree',Helvetica] text-base font-medium text-[var(--gray-primary)]">
            {row.brand.name}
          </span>
        </div>
      ),
    },
    {
      header: 'Status',
      key: 'active',
      render: (active) => (
        <Badge
          className={`h-6 w-[95px] rounded-sm px-0 py-[5px] text-xs font-semibold ${
            active === true
              ? 'bg-[var(--active)] text-[var(--active-dark)]'
              : 'bg-[var(--inactive)] text-[var(--inactive-dark)]'
          }`}
        >
          {active === true ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      header: 'Ações',
      key: 'uuid',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="h-8 w-8 rounded-md p-0 hover:bg-[var(--secondary)]"
            onClick={() => handleEdit(`${value}`)}
          >
            <Icon name="edit" className="text-primary" />
          </Button>
          <Button
            variant="ghost"
            onClick={(e) => canDeleteMaterialType && showModal(e, row.uuid)}
            className={`h-8 w-8 rounded-md p-0 ${canDeleteMaterialType ? 'text-primary' : 'cursor-not-allowed text-[var(--gray-secondary)]'}`}
            disabled={!canDeleteMaterialType}
          >
            <Icon name="delete" />
          </Button>
        </div>
      ),
    },
  ];

  const handleEdit = (uuid: string) => {
    navigate(`${uuid}`);
  };

  const showModal = (e: React.MouseEvent<HTMLButtonElement>, uuid: string) => {
    e.stopPropagation();
    setSelectedUuid(uuid);
    setOpenModal(true);
  };

  const deleteMaterialType = async () => {
    if (!selectedUuid) return;

    deleteMaterialTypeMutation.mutate(
      { uuid: selectedUuid },
      {
        onSuccess: async (_result) => {
          toast.success('Tipo de material removido com sucesso');
          setOpenModal(false);
          setSelectedUuid(null);
          await new Promise((r) => setTimeout(r, 300));
          await refetch();
        },
        onError: (error) => {
          toast.error(error.message);
          setOpenModal(false);
          setSelectedUuid(null);
        },
      }
    );
  };

  useEffect(() => {
    if (location.state?.toastSuccess && !hasShownToast.current) {
      toast.success('Alterações salvas com sucesso');
      hasShownToast.current = true;

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  return (
    <BaseLayout>
      <section className="flex flex-col gap-8">
        <MaterialTypeFilter
          onChange={(filters) => {
            setFilters(filters);
            setPage(1);
          }}
        />
        <section className="w-full">
          {isLoading && <p className="p-4">Carregando materiais...</p>}
          {isError && <p className="p-4 text-red-500">Erro ao carregar materiais.</p>}
          {!isLoading && !isError && (
            <GenericTable
              columns={columns}
              data={materialTypes}
              onPageChange={setPage}
              itemsPerPages={pageSize}
              totalItems={totalItems}
              page={page}
            />
          )}
        </section>
      </section>
      <ConfirmationModal
        title="Deletar tipo de material"
        description="Tem certeza que deseja remover este tipo de material?"
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onConfirm={() => deleteMaterialType()}
        confirmButtonVariant="destructive"
        loading={deleteMaterialTypeMutation.isPending}
      />
    </BaseLayout>
  );
}
